/**
 * Предложение поставить приложение на экран — с обещанием звёзд.
 *
 * Зачем вообще: у человека с айфоном другого приложения нет вовсе (в App Store
 * мы не вышли), а уведомления на iOS работают ТОЛЬКО в установленной PWA — и
 * уведомления пока единственный измеренный нами рычаг удержания (возвращаются
 * 57% согласных против 22% остальных). При этом установок почти нет: трое из
 * ста восьмидесяти шести.
 *
 * **Показывается не сразу и не всем.** Правила те же, что у просьбы включить
 * уведомления, и по той же причине: полосу, которую показывают при каждом
 * запуске, начинают закрывать не глядя, а вместе с ней закроют и всё, что мы
 * туда поместим потом.
 *
 * - только в браузерном режиме (`authMode === 'pwa'`) и только если ещё не
 *   установлено — иначе предлагаем сделанное;
 * - только тому, кто уже что-то сделал (расклад или открытая карта дня):
 *   человек, не увидевший ни одной карты, не понимает, что ему предлагают;
 * - не чаще раза в сутки и не больше трёх раз всего;
 * - анониму не показываем: награду ему всё равно не начислят
 *   (`requireAccount`), а обещание, которое не сбудется, хуже молчания.
 */
import { useEffect, useState } from 'react'

import { canPromptInstall, isIos, isStandalone, promptInstall } from '../lib/install'
import { authMode } from '../lib/auth'
import { isAndroidBrowser } from '../lib/android'
import { useSirr } from '../store'

const SHOWN_KEY = 'sirr_install_offer'
const MAX_SHOWS = 3

interface Shown {
  n: number
  day: string
}

function state(): Shown {
  try {
    const raw = localStorage.getItem(SHOWN_KEY)
    return raw ? (JSON.parse(raw) as Shown) : { n: 0, day: '' }
  } catch {
    return { n: 0, day: '' }
  }
}

/** Пора ли предлагать. Вынесено отдельно, чтобы условие читалось целиком. */
function shouldOffer(done: boolean, anon: boolean, today: string): boolean {
  // На Android этого предложения нет вовсе: там человека зовёт в Play своя
  // полоса (`GetApp`), а приложение из магазина лучше сайта на экране —
  // настоящие уведомления, реклама за звёзды, покупки. Два предложения
  // установки рядом — это выбор, которого человек не просил, и он закроет оба.
  if (isAndroidBrowser()) return false
  if (authMode !== 'pwa' || isStandalone() || anon || !done) return false
  const s = state()
  return s.n < MAX_SHOWS && s.day !== today
}

export function InstallOffer({ bonus }: { bonus: number }) {
  const { t, profile, anon } = useSirr()
  const today = new Date().toISOString().slice(0, 10)
  // Человек уже что-то сделал: сделал расклад или хотя бы открыл карту дня.
  const done = profile.history.length > 0 || Boolean(profile.dayFlipped[today])
  const [open, setOpen] = useState(() => shouldOffer(done, anon, today))

  // Показ засчитываем ПРИ ПОЯВЛЕНИИ, а не по кнопке «не сейчас»: человек может
  // просто уйти с экрана, и это тоже ответ. Иначе одно и то же предложение
  // показывалось бы бесконечно.
  useEffect(() => {
    if (!open) return
    const s = state()
    try {
      localStorage.setItem(SHOWN_KEY, JSON.stringify({ n: s.n + 1, day: today }))
    } catch {
      /* приватный режим — покажем ещё раз, это не страшно */
    }
  }, [open, today])

  if (!open || bonus <= 0) return null

  const ios = isIos()

  return (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
        background: 'rgba(9, 5, 18, 0.82)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 340,
          textAlign: 'center',
          border: '1px solid var(--line-strong)',
          borderRadius: 20,
          padding: '26px 22px',
          background: 'rgba(24, 14, 38, 0.98)',
        }}
      >
        <div style={{ fontSize: 34, color: 'var(--gold-bright)', lineHeight: 1 }}>✦</div>
        <div
          style={{
            fontFamily: 'var(--serif)',
            fontStyle: 'italic',
            fontSize: 21,
            color: 'var(--ink)',
            marginTop: 10,
            lineHeight: 1.25,
          }}
        >
          {t('installTitle', { n: bonus })}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-muted)', marginTop: 10 }}>
          {t('installBody')}
        </div>

        {/*
          На iOS системного окна установки не существует — там только инструкция
          «Поделиться → На экран «Домой»». Поэтому кнопки нет, а текст берётся
          тот же, что в баннере: две разные инструкции разъехались бы.
        */}
        {ios ? (
          <div style={{ fontSize: 13, color: 'var(--ink)', marginTop: 14, lineHeight: 1.5 }}>
            {t('installIos')}
          </div>
        ) : (
          <button
            type="button"
            disabled={!canPromptInstall()}
            onClick={() => {
              void promptInstall()
              setOpen(false)
            }}
            style={{
              marginTop: 18,
              width: '100%',
              background: 'var(--gold-gradient)',
              color: '#1a1020',
              border: 'none',
              borderRadius: 14,
              padding: '13px 20px',
              fontFamily: 'var(--sans)',
              fontSize: 15.5,
              fontWeight: 600,
              opacity: canPromptInstall() ? 1 : 0.5,
            }}
          >
            {t('installGo')}
          </button>
        )}

        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            marginTop: 10,
            width: '100%',
            background: 'transparent',
            color: 'var(--ink-faint)',
            border: 'none',
            padding: '10px 20px',
            fontFamily: 'var(--sans)',
            fontSize: 14,
          }}
        >
          {t('installLater')}
        </button>
      </div>
    </div>
  )
}
